import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
  page:         { padding: 40, fontSize: 10, fontFamily: 'Helvetica', backgroundColor: '#ffffff' },
  header:       { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 },
  title:        { fontSize: 26, fontWeight: 'bold', color: '#374151' },
  subtitle:     { fontSize: 12, color: '#6b7280', marginTop: 4 },
  bizName:      { fontSize: 14, fontWeight: 'bold', textAlign: 'right' },
  bizDetail:    { fontSize: 9, color: '#6b7280', textAlign: 'right', marginTop: 2 },
  divider:      { borderBottomWidth: 2, borderColor: '#374151', marginBottom: 20 },
  infoRow:      { flexDirection: 'row', gap: 20, marginBottom: 24 },
  infoBlock:    { flex: 1 },
  infoLabel:    { fontSize: 8, color: '#9ca3af', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 3 },
  infoValue:    { fontSize: 11, color: '#111827' },
  infoSub:      { fontSize: 9, color: '#6b7280', marginTop: 2, lineHeight: 1.5 },
  tableHead:    { flexDirection: 'row', backgroundColor: '#374151', paddingVertical: 8, paddingHorizontal: 10 },
  thText:       { color: '#fff', fontSize: 8, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 0.5 },
  row:          { flexDirection: 'row', paddingVertical: 9, paddingHorizontal: 10, borderBottomWidth: 1, borderColor: '#f0f0f0' },
  rowAlt:       { backgroundColor: '#f9fafb' },
  colProduct:   { flex: 5 },
  colSku:       { flex: 2 },
  colQty:       { flex: 1, textAlign: 'center' },
  colPicked:    { width: 40, textAlign: 'center', alignItems: 'center' },
  colPacked:    { width: 40, textAlign: 'center', alignItems: 'center' },
  checkBox:     { width: 13, height: 13, borderWidth: 1, borderColor: '#374151', borderRadius: 2 },
  productName:  { fontSize: 10, color: '#111827', fontWeight: 'bold' },
  productDesc:  { fontSize: 8, color: '#9ca3af', marginTop: 1 },
  skuText:      { fontSize: 9, color: '#6b7280' },
  qtyText:      { fontSize: 11, fontWeight: 'bold', color: '#111827', textAlign: 'center' },
  summaryRow:   { flexDirection: 'row', justifyContent: 'flex-end', gap: 32, marginTop: 16, paddingTop: 12, borderTopWidth: 1, borderColor: '#e5e7eb' },
  sumItem:      { alignItems: 'center' },
  sumLabel:     { fontSize: 9, color: '#6b7280', textTransform: 'uppercase' },
  sumValue:     { fontSize: 14, fontWeight: 'bold', color: '#111827', marginTop: 2 },
  notesBox:     { marginTop: 20, padding: 12, backgroundColor: '#fffbeb', borderWidth: 1, borderColor: '#fde68a', borderRadius: 4 },
  notesLabel:   { fontSize: 8, color: '#92400e', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: 4 },
  notesText:    { fontSize: 10, color: '#78350f' },
  footer:       { position: 'absolute', bottom: 30, left: 40, right: 40 },
  footerLine:   { borderTopWidth: 1, borderColor: '#e5e7eb', paddingTop: 8, flexDirection: 'row', justifyContent: 'space-between' },
  footerText:   { fontSize: 8, color: '#9ca3af' },
});

const fmt = (v: any) =>
  v ? new Date(v).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const PackingSlipPDF: React.FC<{ order: any; businessSettings?: any }> = ({ order, businessSettings }) => {
  const items: any[] = Array.isArray(order?.items) ? order.items : [];
  const orderNumber   = order?.order_number || order?.orderNumber || String(order?.id || '—');
  const customerName  = order?.customer_name || order?.customerName || '—';
  const shippingAddr  = order?.shipping_address || order?.shippingAddress || order?.billing_address || order?.billingAddress || '';
  const orderDate     = order?.order_date || order?.orderDate || order?.created_at;
  const notes         = order?.notes || '';
  const totalQty      = items.reduce((s: number, it: any) => s + Number(it.qty || it.quantity || 0), 0);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* ── Header ── */}
        <View style={styles.header}>
          <View>
            <Text style={styles.title}>PACKING SLIP</Text>
            <Text style={styles.subtitle}>Order #{orderNumber}</Text>
          </View>
          <View>
            {businessSettings?.name   && <Text style={styles.bizName}>{businessSettings.name}</Text>}
            {businessSettings?.email  && <Text style={styles.bizDetail}>{businessSettings.email}</Text>}
            {businessSettings?.phone  && <Text style={styles.bizDetail}>Tel: {businessSettings.phone}</Text>}
            {businessSettings?.address && <Text style={styles.bizDetail}>{businessSettings.address}</Text>}
          </View>
        </View>

        <View style={styles.divider} />

        {/* ── Info Row ── */}
        <View style={styles.infoRow}>
          <View style={styles.infoBlock}>
            <Text style={styles.infoLabel}>Ship To</Text>
            <Text style={styles.infoValue}>{customerName}</Text>
            {shippingAddr ? (
              <Text style={styles.infoSub}>{shippingAddr.replace(/,\s*/g, '\n')}</Text>
            ) : null}
          </View>
          <View style={styles.infoBlock}>
            <Text style={styles.infoLabel}>Order Date</Text>
            <Text style={styles.infoValue}>{fmt(orderDate)}</Text>
            <Text style={[styles.infoLabel, { marginTop: 10 }]}>Payment</Text>
            <Text style={styles.infoValue}>{order?.payment_method || order?.paymentMethod || '—'}</Text>
          </View>
          <View style={styles.infoBlock}>
            <Text style={styles.infoLabel}>Printed</Text>
            <Text style={styles.infoValue}>{new Date().toLocaleDateString('en-GB')}</Text>
            <Text style={[styles.infoLabel, { marginTop: 10 }]}>Status</Text>
            <Text style={styles.infoValue}>{order?.status || '—'}</Text>
          </View>
        </View>

        {/* ── Items Table ── */}
        <View style={styles.tableHead}>
          <Text style={[styles.thText, styles.colProduct]}>Product</Text>
          <Text style={[styles.thText, styles.colSku]}>SKU / Ref</Text>
          <Text style={[styles.thText, styles.colQty]}>Qty</Text>
          <Text style={[styles.thText, { width: 40, textAlign: 'center' }]}>Picked</Text>
          <Text style={[styles.thText, { width: 40, textAlign: 'center' }]}>Packed</Text>
        </View>

        {items.map((item: any, i: number) => (
          <View key={i} style={[styles.row, i % 2 === 1 ? styles.rowAlt : {}]}>
            <View style={styles.colProduct}>
              <Text style={styles.productName}>
                {item.productName || item.product_name || item.name || 'Item'}
              </Text>
              {item.description ? <Text style={styles.productDesc}>{item.description}</Text> : null}
              {item.saleType === 'box' && item.box_quantity
                ? <Text style={styles.productDesc}>Box of {item.box_quantity}</Text>
                : null}
            </View>
            <View style={styles.colSku}>
              <Text style={styles.skuText}>{item.sku || item.productId || item.product_id || ''}</Text>
            </View>
            <View style={styles.colQty}>
              <Text style={styles.qtyText}>{item.qty || item.quantity || 1}</Text>
            </View>
            <View style={styles.colPicked}>
              <View style={styles.checkBox} />
            </View>
            <View style={styles.colPacked}>
              <View style={styles.checkBox} />
            </View>
          </View>
        ))}

        {/* ── Summary ── */}
        <View style={styles.summaryRow}>
          <View style={styles.sumItem}>
            <Text style={styles.sumLabel}>Lines</Text>
            <Text style={styles.sumValue}>{items.length}</Text>
          </View>
          <View style={styles.sumItem}>
            <Text style={styles.sumLabel}>Total Units</Text>
            <Text style={styles.sumValue}>{totalQty}</Text>
          </View>
        </View>

        {/* ── Notes ── */}
        {notes && notes !== 'Online shop order' ? (
          <View style={styles.notesBox}>
            <Text style={styles.notesLabel}>Special Instructions</Text>
            <Text style={styles.notesText}>{notes}</Text>
          </View>
        ) : null}

        {/* ── Footer ── */}
        <View style={styles.footer}>
          <View style={styles.footerLine}>
            <Text style={styles.footerText}>
              Packing Slip — Order #{orderNumber} — {businessSettings?.name || 'ShahoSuite'}
            </Text>
            <Text style={styles.footerText}>
              Please check all items before dispatch. Thank you for your business.
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default PackingSlipPDF;
